import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { useMutation } from '@tanstack/react-query';
import { BASE_URL, BASE_URL_V2 } from 'shared/constants';

export const useSupplierDynamicsApi = () => {
  const { axios } = useAxios(BASE_URL);
  const { axios: axiosV2 } = useAxios(BASE_URL_V2);

  /**
   * Fetch Deal Dynamic
   * @param id
   */
  const fetchDealDynamic = async (id: number) => {
    try {
      const res = await axios.get(`/api/deal_dynamic/${id}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Inserts a supplier selection with the provided details.
   * @param {number} [supplier_id] - The ID of the supplier to be selected.
   * @param {string} [vendor_name] - The name of the vendor.
   * @param {string} [vendor_domain] - The domain of the vendor.
   * @returns {Promise<any>} A promise that resolves with the inserted supplier selection data if the operation is successful, or rejects with an error if the operation fails.
   */
  const insertSupplierSelection = async (
    supplier_id?: number | null,
    vendor_name?: string | null,
    vendor_domain?: string | null
  ): Promise<any> => {
    try {
      const res = await axios.post(`/api/supplierDynamics/supplier/selection`, {
        supplier_id,
        vendor_name,
        vendor_domain
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      await AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch Supplier Deal Dynamic
   * @param id
   * @param supplier_category_id
   */
  const fetchSupplierDealDynamics = async (id: number, supplier_category_id?: number) => {
    try {
      const res = await axios.get(`/api/supplier/${id}/deal_dynamics`, {
        params: { supplier_category_id }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch Supplier Deal Dynamic statistics
   *
   */
  const fetchSupplierDynamicsStats = async () => {
    try {
      const res = await axios.get(`/api/supplier_dynamics/statistics`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch Supplier Briefing
   * @param id
   */
  const fetchSupplierBriefing = async (id: number) => {
    try {
      const res = await axios.get(`/api/supplier/${id}/briefing`);
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch supplier briefing'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const fetchSupplierRiskDetails = async (id: number) => {
    try {
      const res = await axios.get(`/api/supplier/${id}/report?report_type=Risk`);
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch supplier risk details'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const insertSupplierBriefingFeedback = async (id: number, score: number, feedback?: string) => {
    try {
      const res = await axios.post(`/api/supplier_briefing/${id}/feedback`, {
        score: score,
        feedback: feedback
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const insertSupplierReportFeedback = async (id: number, score: number, feedback?: string) => {
    try {
      const res = await axiosV2.post(`/supplier/report/${id}/feedback`, {
        score: score,
        feedback: feedback
      });
      if (res?.status === 201) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to insert supplier report feedback'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch supplier notes
   * @param supplier_id
   */
  const fetchSupplierNotes = async (supplier_id: number) => {
    try {
      const res = await axios.get(`/note/supplier/${supplier_id}`, { baseURL: BASE_URL_V2 });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch supplier notes'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Create supplier note
   * @param supplierId
   * @param dealNote
   * @param dealNote.note
   * @param dealNote.parent_id
   * @param dealNote.is_visible_to_npi
   */
  const createSupplierNote = async (
    supplierId: number,
    dealNote: { note: string; parent_id?: number; is_visible_to_npi: boolean }
  ) => {
    try {
      const url = `/note/supplier/${supplierId}/note`;
      const res = await axios.post(url, dealNote, { baseURL: BASE_URL_V2 });
      if (res?.status === 201) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to create supplier note'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Delete supplier note
   * @param supplierId
   * @param noteId
   */
  const deleteSupplierNote = async (supplierId: number, noteId: number) => {
    try {
      const url = `/note/supplier/note/${noteId}`;
      const res = await axios.delete(url, { baseURL: BASE_URL_V2 });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to delete supplier note'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Update supplier note
   * @param supplierId
   * @param noteId
   * @param payload
   * @param payload.note
   * @param payload.is_visible_to_npi
   */
  const updateSupplierNote = async (
    supplierId: number,
    noteId: number,
    payload: { note: string; is_visible_to_npi: boolean }
  ) => {
    try {
      const url = `/note/supplier/note/${noteId}`;
      const res = await axios.patch(url, payload, { baseURL: BASE_URL_V2 });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to update supplier note'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  const updateSupplierIqReport = async (id: number, briefing: string): Promise<any> => {
    try {
      const res = await axiosV2.patch(`/supplier/${id}/report/iq`, {
        briefing
      });
      if (res?.status === 200 || res?.data?.status === 'success') {
        return res.data?.data || res.data;
      }
      const errorMessage = res?.data?.message || 'Failed to update supplier IQ report';
      return Promise.reject(Error(errorMessage));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const updateSupplierRiskReport = async (
    id: number,
    report: Array<{ score: number; title: string; payload: string }>
  ): Promise<any> => {
    try {
      const res = await axiosV2.patch(`/supplier/${id}/report/risk`, {
        report
      });
      if (res?.data?.status === 'success' || res?.status === 200) {
        return res.data?.data || res.data;
      }
      const errorMessage = res?.data?.message || 'Failed to update supplier risk report';
      return Promise.reject(Error(errorMessage));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  return {
    fetchDealDynamic,
    insertSupplierSelection,
    fetchSupplierDealDynamics,
    fetchSupplierDynamicsStats,
    fetchSupplierBriefing,
    fetchSupplierRiskDetails,
    insertSupplierBriefingFeedback,
    insertSupplierReportFeedback,
    fetchSupplierNotes,
    createSupplierNote,
    deleteSupplierNote,
    updateSupplierNote,
    updateSupplierIqReport,
    updateSupplierRiskReport
  };
};

export const useSupplierBriefingFeedback = (config = {}) => {
  const { insertSupplierBriefingFeedback } = useSupplierDynamicsApi();
  return useMutation({
    mutationFn: async (data: { id: number; score: number; feedback?: string }) => {
      const { id, score, feedback } = data;
      return await insertSupplierBriefingFeedback(id, score, feedback);
    },
    ...config
  });
};

export const useSupplierReportFeedback = (config = {}) => {
  const { insertSupplierReportFeedback } = useSupplierDynamicsApi();
  return useMutation({
    mutationFn: async (data: { id: number; score: number; feedback?: string }) => {
      const { id, score, feedback } = data;
      return await insertSupplierReportFeedback(id, score, feedback);
    },
    ...config
  });
};
